import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigation } from '@/contexts/NavigationContext';
import { supportTicketService } from '@/services/support-ticket.service';
import type { CreateLinearIssueInput, SupportTicketFilters } from '@/types/support-ticket';

export function useSupportTickets(
  filters: SupportTicketFilters = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ['support-tickets', filters],
    queryFn: () => supportTicketService.list(filters),
    refetchInterval: 30000,
    enabled: options.enabled ?? true,
  });
}

export function useSupportTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () => supportTicketService.getTicket(id as string),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.status === 'closed' ? false : 15000,
  });
}

export function useSupportTicketAssignees() {
  return useQuery({
    queryKey: ['support-ticket-assignees'],
    queryFn: () => supportTicketService.getAssignees(),
  });
}

function useInvalidateTicket() {
  const queryClient = useQueryClient();
  return (id: string) => {
    queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['support-ticket', id] });
  };
}

export function useAssignTicket() {
  const invalidate = useInvalidateTicket();
  return useMutation({
    mutationFn: ({ id, assigneeUserId }: { id: string; assigneeUserId?: string }) =>
      supportTicketService.assign(id, assigneeUserId),
    onSuccess: (_data, vars) => invalidate(vars.id),
  });
}

export function useCloseTicket() {
  const invalidate = useInvalidateTicket();
  return useMutation({
    mutationFn: ({ id, resolutionNote }: { id: string; resolutionNote: string }) =>
      supportTicketService.close(id, resolutionNote),
    onSuccess: (_data, vars) => invalidate(vars.id),
  });
}

export function useCreateLinearIssue() {
  const invalidate = useInvalidateTicket();
  return useMutation({
    mutationFn: ({ id, category, notes }: { id: string } & CreateLinearIssueInput) =>
      supportTicketService.createLinearIssue(id, { category, notes }),
    onSuccess: (_data, vars) => invalidate(vars.id),
  });
}

export function useReplyTicket() {
  const invalidate = useInvalidateTicket();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      message,
      files,
    }: {
      id: string;
      message: string;
      files?: File[];
    }) => supportTicketService.reply(id, message, files),
    onSuccess: (data, vars) => {
      // The reply auto-assigns an unassigned ticket; reflect the returned detail immediately.
      if (data) queryClient.setQueryData(['support-ticket', vars.id], data);
      invalidate(vars.id);
    },
  });
}

/** Nav is server-gated by Modules.SUPPORT_TICKETS (node uid 'support-tickets'). */
export function useCanSeeTickets(): boolean {
  const { sections } = useNavigation();
  return sections.some((s) => s.nodes?.some((n) => n.uid === 'support-tickets'));
}
